import {
  reviewSummary,
  type FieldDefinition,
  type Metafield,
  type Variant,
} from "@estelle/domain";
import { Star } from "lucide-react";

import { renderCell } from "@/components/table/cells";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * The parts of a product both the detail drawer and the list's cards show —
 * stars, price, tags — so a product reads the same in all three places.
 */

const PRICE_FIELD: FieldDefinition = {
  name: "price",
  label: "Price",
  type: "money",
};

/**
 * Stars, the way a storefront shows them. Two rows of the same icons stacked,
 * the filled one clipped to the rating's share of the scale, so 4.8 reads as
 * four and most of a fifth rather than rounding up to five.
 */
export function RatingStars({
  metafields,
  size = "md",
}: {
  metafields: Metafield[];
  size?: "sm" | "md";
}) {
  const summary = reviewSummary(metafields);
  const rating = summary
    ? { value: summary.rating, max: summary.scaleMax }
    : null;
  const count = summary?.count ?? 0;
  const text = size === "sm" ? "text-xs" : "text-sm";

  if (!rating) {
    return <p className={cn("text-muted-foreground", text)}>No reviews yet</p>;
  }

  const stars = Math.round(rating.max);
  const fill = `${String(Math.min(100, (rating.value / rating.max) * 100))}%`;
  const icon = size === "sm" ? "size-3.5" : "size-4";
  const row = (className: string) => (
    <div className="flex">
      {Array.from({ length: stars }, (_, index) => (
        <Star key={index} className={cn(icon, "shrink-0", className)} />
      ))}
    </div>
  );

  return (
    <div
      className={cn("flex items-center gap-1.5", text)}
      aria-label={`Rated ${rating.value.toFixed(1)} out of ${String(stars)}, ${String(count)} review${count === 1 ? "" : "s"}`}
    >
      <div className="relative" aria-hidden>
        {row("text-muted-foreground/40")}
        <div
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{ width: fill }}
        >
          {row("fill-amber-400 text-amber-400")}
        </div>
      </div>
      <span className="font-medium tabular-nums" aria-hidden>
        {rating.value.toFixed(1)}
      </span>
      <span className="text-muted-foreground" aria-hidden>
        ({count}
        {size === "sm" ? "" : ` review${count === 1 ? "" : "s"}`})
      </span>
    </div>
  );
}

/**
 * The price a customer pays, with the compare-at price struck through beside
 * it when it is higher. A single variant — every product in this store today —
 * shows its own pair; several show the range and no compare-at, since one
 * struck-through figure against a range would claim a saving some variants do
 * not have.
 */
export function PriceSummary({
  variants,
  size = "md",
}: {
  variants: Variant[];
  size?: "sm" | "md";
}) {
  const main = cn(
    "text-primary-strong font-semibold tabular-nums",
    size === "sm" ? "text-base" : "text-xl",
  );
  const [only] = variants;

  if (variants.length === 1 && only) {
    const discounted =
      only.compareAtPrice !== null &&
      Number(only.compareAtPrice) > Number(only.price);
    return (
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className={main}>{renderCell(PRICE_FIELD, only.price)}</span>
        {discounted ? (
          <span
            className={cn(
              "text-muted-foreground tabular-nums line-through",
              size === "sm" ? "text-xs" : "text-sm",
            )}
          >
            {renderCell(PRICE_FIELD, only.compareAtPrice)}
          </span>
        ) : null}
      </div>
    );
  }

  const prices = variants
    .map((variant) => variant.price)
    .sort((a, b) => Number(a) - Number(b));
  const low = prices[0];
  const high = prices[prices.length - 1];
  if (low === undefined || high === undefined) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className={main}>
      {renderCell(PRICE_FIELD, low)}
      {Number(high) !== Number(low) ? (
        <> – {renderCell(PRICE_FIELD, high)}</>
      ) : null}
    </div>
  );
}

export function ProductTags({ tags }: { tags: string[] }) {
  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map((tag) => (
        <Badge
          key={tag}
          variant="outline"
          className="border-primary/40 bg-primary/10 text-primary-strong font-medium"
        >
          {tag}
        </Badge>
      ))}
    </div>
  );
}
